import React, { useState, useEffect, useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import _ from "lodash"
import { Box, Button, Grid } from "@mui/material"
import ButtonGroup from "@mui/material/ButtonGroup"
import CommonTitle from "components/common/CommonTitle"
import CommonHelp from "components/common/CommonHelp"
import TextField from "@mui/material/TextField"
import Select from "@mui/material/Select"
import FormControl from "@mui/material/FormControl"
import ErrorMessage from "components/ErrorMessage"
import { createRequestCarList } from "utils/JsonManipulation"
import RightArrowButton from "components/Button/RightArrowButton"
import Loading from "components/Loading"
import { APP_URL, Colors } from "Const"
import { AppState } from "store/store"
import { isErrorInCarListFromModel } from "utils/JsonErrorCheck"
import { qrActions } from "store/actions/QRAction"
import { isEmpty, hyphenIsAtBeginning } from "utils/Validators"
import isHttpError from "utils/HttpErrorCheck"
import isHttpMaintenance from "utils/HttpMaintenanceCheck"
import getCarNmList from "apis/getCarNmList"
import { extractNumbersAndAlphabets } from "utils/InputControl"
import styles from "./QRScanResult.module.scss"

export interface QRScanResultProps {
  submit: (
    valueVehicleType: string,
    valueCarModel: string,
    valueFirstInspectionYear: string,
    valueFirstInspectionMonth: string,
    valueCarName: string,
    valueAutomaticBraking: string,
    valueAebButtonSelected: string,
    valueIsScan: boolean,
    valuleAEBVisible: boolean
  ) => void
  openModal: () => void
}
// stateだと再描画の前に値が変更されないため通常の変数を使う
let currentAbe = 0
let currentValueType = -1
let operationPeriod = ""
/**
 * カメラスキャン時のデバック表示(local,dev環境のみ)
 * @param {(...) => void} submit - submit時に車検証情報を渡して呼び出し元で処理を実行
 * @param {() => void} openModal - モーダルを開く時のイベントを呼び出し元へ伝える
 */
const QRScanResult = ({ submit, openModal }: QRScanResultProps) => {
  const dispatch = useDispatch()
  const vehicleTypeRef = React.createRef<HTMLHeadingElement>()
  const carModelRef = React.createRef<HTMLHeadingElement>()
  const carNameRef = React.createRef<HTMLHeadingElement>()
  const automaticBrakingRef = React.createRef<HTMLHeadingElement>()
  const defaultfirstInspectionRef = React.createRef<HTMLHeadingElement>()
  const scrollToVehicleTypeRef = React.useCallback(() => {
    if (vehicleTypeRef && vehicleTypeRef.current) {
      vehicleTypeRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    }
  }, [vehicleTypeRef])
  const scrollToCarModelRef = React.useCallback(() => {
    if (carModelRef && carModelRef.current) {
      carModelRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    }
  }, [carModelRef])
  const scrollToCarNameRef = React.useCallback(() => {
    if (carNameRef && carNameRef.current) {
      carNameRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    }
  }, [carNameRef])
  const scrollToAutomaticBrakingRef = React.useCallback(() => {
    if (automaticBrakingRef && automaticBrakingRef.current) {
      automaticBrakingRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    }
  }, [automaticBrakingRef])
  const scrollToDefaultfirstInspectionRef = React.useCallback(() => {
    if (defaultfirstInspectionRef && defaultfirstInspectionRef.current) {
      defaultfirstInspectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    }
  }, [defaultfirstInspectionRef])
  const {
    automaticBraking,
    vehicleType,
    carModel,
    firstInspectionYear,
    firstInspectionMonth,
    carName,
    aebButtonSelected,
    isScan
  } = useSelector((state: AppState) => state.qr)
  const setVehicleType = useCallback((value: string) => dispatch(qrActions.updateVehicleType(value)), [dispatch])
  const setCarModel = useCallback((value: string) => dispatch(qrActions.updateCarModel(value)), [dispatch])
  const setFirstInspectionYear = useCallback(
    (value: string) => dispatch(qrActions.updateFirstInspectionYear(value)),
    [dispatch]
  )
  const setFirstInspectionMonth = useCallback(
    (value: string) => dispatch(qrActions.updateFirstInspectionMonth(value)),
    [dispatch]
  )
  const setCarName = useCallback((value: string) => dispatch(qrActions.updateCarName(value)), [dispatch])

  const [showErrors, setShowErrors] = useState(false)
  const [valueAbe, setValueAbe] = useState(0)
  const [abeVisible, setAbeVisible] = useState(false)
  const [carNameList, setCarNameList] = useState([])
  const [valueAutomaticBraking, setValueAutomaticBraking] = useState("")
  const [isNoSearchResult, setIsNoSearchResult] = useState(false)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const UUID = useSelector((state: AppState) => state.uuid.uuid)

  const getCurrentYear = (): number => {
    const date = new Date()
    return date.getFullYear()
  }

  const addEra = (year: number): string => {
    if (year > 2019) {
      return `${year} (令和${year - 2018})`
    }
    if (year === 2019) {
      return `${year} (令和元)`
    }
    if (year > 1989) {
      return `${year} (平成${year - 1988})`
    }
    if (year === 1989) {
      return `${year} (平成元)`
    }
    if (year > 1926) {
      return `${year} (昭和${year - 1925})`
    }
    if (year === 1926) {
      return `${year} (昭和元)`
    }
    return `${year} (大正${year - 1912})`
  }

  const disabledCheck = (inputName: string): boolean => {
    if (inputName === "vehicleType") {
      return isScan
    }
    if (inputName === "carModel") {
      return isScan || isEmpty(vehicleType)
    }
    if (inputName === "carName") {
      return (isScan && carNameList.length === 1) || carNameList.length <= 1 || isEmpty(carModel)
    }
    if (inputName === "firstInspectionYear") {
      return isScan || isEmpty(carName)
    }
    if (inputName === "firstInspectionMonth") {
      return isScan || isEmpty(firstInspectionYear)
    }
    if (inputName === "automaticBraking") {
      return isEmpty(firstInspectionMonth)
    }
    return false
  }

  const abeVisibleCheck = (period: string) => {
    const date = new Date()
    // 保険始期日は本日から30日後を設定
    date.setDate(date.getDate() + 30)
    // date.setDate(date.getDate() - 30000)
    const month = `0${date.getMonth() + 1}`.slice(-2)
    const day = `0${date.getDate()}`.slice(-2)
    const formatted = `${date.getFullYear()}/${month}/${day}`
    console.log(`適用期間:${period}  始期日:${formatted} AEB装置:${currentAbe}`)
    if (period < formatted) {
      setValueAbe(0)
      setAbeVisible(false)
      console.log(`適用期間 < 始期日`)
    } else if (currentAbe === 1) {
      setValueAbe(1)
      setAbeVisible(false)
      console.log(`AEB装置 = 1: ${currentAbe}`)
    } else if (currentAbe === 2) {
      setAbeVisible(true)
      console.log(`AEB装置 = 2: ${currentAbe}`)
    } else {
      setValueAbe(0)
      setAbeVisible(false)
      console.log(`AEB装置 = ?: ${currentAbe}`)
    }
  }

  const getCarList = async (selectedModel: string) => {
    // 型式が空の時、APIコールを実行しない
    if (isEmpty(selectedModel)) {
      // エラーメッセージ非表示
      setIsNoSearchResult(false)
      return
    }
    setLoading(true)
    currentAbe = 0
    currentValueType = -1
    operationPeriod = ""
    const req = createRequestCarList(carModel, UUID)
    console.log("REQUEST（型式から車名一覧取得API）:", req)
    // FIXME: 'res' is of type 'unknown'.
    const res: any = await getCarNmList(req)
    console.log("RESPONSE（型式から車名一覧取得API）:", res)

    if (isHttpMaintenance(res)) {
      // HTTP MAINTENANCE CHECK
      navigate(APP_URL.MAINTENANCE)
    } else if (isHttpError(res)) {
      // HTTP ERROR CHECK
      navigate(APP_URL.ERROR, {
        state: { api: "車名の取得", message: "" }
      })
    } else if (isErrorInCarListFromModel(res)) {
      // Json ERROR CHECK
      // スキャンの場合はエラー画面に遷移させる
      if (isScan) {
        navigate(APP_URL.ERROR, {
          state: { api: "車名の取得", message: "" }
        })
      }
      setCarNameList([])
      setIsNoSearchResult(true)
    } else {
      setIsNoSearchResult(false)
      const list = (res as any).data["車名一覧"]["自動車-車名一覧"]
      setCarNameList(list)
      if (list.length === 1) {
        operationPeriod = list[0]["適用期間"]
        currentAbe = Number(list[0]["AEB装置"])
        currentValueType = 0
        setCarName(list[0]["自動車-車名"])
      } else if (carName) {
        const idx = list.findIndex((x: any) => x["自動車-車名"] === carName)
        if (idx !== -1) {
          operationPeriod = list[idx]["適用期間"]
          currentAbe = Number(list[idx]["AEB装置"])
          currentValueType = idx
        }
      }
      abeVisibleCheck(operationPeriod)
      if (aebButtonSelected && !disabledCheck("automaticBraking")) {
        setValueAutomaticBraking(aebButtonSelected)
        if (aebButtonSelected === "yes") {
          setValueAbe(1)
        } else if (aebButtonSelected === "no" || aebButtonSelected === "unKnown") {
          setValueAbe(0)
        }
      }
    }
    setLoading(false)
  }

  useEffect(() => {
    if (carModel) {
      getCarList(carModel)
    }
  }, [])

  // 車名を選択した場合のリセット
  const resetByCarName = () => {
    if (!isScan) {
      setFirstInspectionYear("-1")
      setFirstInspectionMonth("-1")
    }
    setValueAutomaticBraking("")
    operationPeriod = ""
  }

  // 型式を選択した場合のリセット
  const resetByCarModel = () => {
    if (!isScan) {
      setCarName("")
      setCarNameList([])
      resetByCarName()
      currentValueType = -1
    }
  }

  // 用途車種を選択した場合のリセット
  const resetByVehicleType = () => {
    if (!isScan) {
      setCarModel("")
      resetByCarModel()
    }
  }

  useEffect(() => {
    abeVisibleCheck(operationPeriod)
  }, [carName])
  const scrollCheck = () => {
    if (isEmpty(vehicleType)) scrollToVehicleTypeRef()
    else if (isEmpty(carModel)) scrollToCarModelRef()
    else if (isEmpty(carName)) scrollToCarNameRef()
    else if (isEmpty(firstInspectionYear) || isEmpty(firstInspectionMonth)) scrollToDefaultfirstInspectionRef()
    else if (abeVisible && isEmpty(valueAutomaticBraking)) scrollToAutomaticBrakingRef()
  }
  const isAllowedToGoNextPage = () => {
    const emptyError =
      isEmpty(vehicleType) ||
      isEmpty(carModel) ||
      isEmpty(firstInspectionYear) ||
      isEmpty(firstInspectionMonth) ||
      isEmpty(carName) ||
      (abeVisible && isEmpty(valueAutomaticBraking))
    if (emptyError) scrollCheck()
    return !emptyError
  }

  const goToConditions = () => {
    setShowErrors(true)
    if (isAllowedToGoNextPage()) {
      submit(
        vehicleType,
        carModel,
        firstInspectionYear,
        firstInspectionMonth,
        carName,
        String(valueAbe),
        valueAutomaticBraking,
        isScan,
        abeVisible
      )
    }
  }

  const description = () => {
    if (!isScan) {
      return "お車の情報をご入力ください。"
    }
    return "車検証からお車の情報を読み取りました。以下の内容をご確認ください。"
  }

  const renderFirstInspection = () => {
    return (
      <>
        <h3 ref={defaultfirstInspectionRef}>初度登録年月（初度検査年月）</h3>
        <Box className={styles.dateForms}>
          <div className={styles.yearArea}>
            <FormControl variant="outlined" className={styles.formControl}>
              {(() => {
                const items = []
                for (let i = getCurrentYear(); i > getCurrentYear() - 100; i -= 1) {
                  items.push(
                    <option key={i} value={i}>
                      {addEra(i)}年
                    </option>
                  )
                }
                return (
                  <Select
                    onChange={(event) => setFirstInspectionYear(event.target.value)}
                    value={firstInspectionYear}
                    native
                    disabled={disabledCheck("firstInspectionYear")}
                    label="firstInspectionYear"
                    className={`${disabledCheck("firstInspectionYear") && styles.disabled} ${styles.inputYear}`}
                  >
                    <option key={-1} value={-1}>
                      {" "}
                    </option>
                    {items}
                  </Select>
                )
              })()}
            </FormControl>
          </div>
          <FormControl variant="outlined" className={styles.formControl}>
            {(() => {
              return (
                <Select
                  onChange={(event) => setFirstInspectionMonth(event.target.value)}
                  value={firstInspectionMonth}
                  native
                  disabled={disabledCheck("firstInspectionMonth")}
                  label="firstInspectionMonth"
                  className={`${disabledCheck("firstInspectionMonth") && styles.disabled} ${styles.inputMonth}`}
                >
                  <option key={-1} value={-1}>
                    {" "}
                  </option>
                  <option key="1" value="01">
                    1月
                  </option>
                  <option key="2" value="02">
                    2月
                  </option>
                  <option key="3" value="03">
                    3月
                  </option>
                  <option key="4" value="04">
                    4月
                  </option>
                  <option key="5" value="05">
                    5月
                  </option>
                  <option key="6" value="06">
                    6月
                  </option>
                  <option key="7" value="07">
                    7月
                  </option>
                  <option key="8" value="08">
                    8月
                  </option>
                  <option key="9" value="09">
                    9月
                  </option>
                  <option key="10" value="10">
                    10月
                  </option>
                  <option key="11" value="11">
                    11月
                  </option>
                  <option key="12" value="12">
                    12月
                  </option>
                </Select>
              )
            })()}
          </FormControl>
        </Box>
        <ErrorMessage isUnselected={showErrors && (isEmpty(firstInspectionYear) || isEmpty(firstInspectionMonth))} />
      </>
    )
  }
  return (
    <>
      <Loading loadingFlag={loading} />
      <CommonTitle titles={["お車について"]} read={description()} />
      <CommonHelp
        text="車検証のご確認方法"
        click={() => {
          openModal()
        }}
      />
      <hr />
      <h3 ref={vehicleTypeRef}>用途車種</h3>
      <FormControl variant="outlined" className={styles.formControl}>
        <Select
          onChange={(event) => {
            setVehicleType(event.target.value)
            resetByVehicleType()
          }}
          defaultValue={vehicleType}
          disabled={disabledCheck("vehicleType")}
          native
          label="vehicleType"
          className={`${disabledCheck("vehicleType") && styles.disabled} ${styles.input}`}
        >
          {/* eslint-disable jsx-a11y/control-has-associated-label */}
          <option value={-1}> </option>
          <option value="11">自家用普通乗用車</option>
          <option value="13">自家用小型乗用車</option>
          <option value="49">自家用小型貨物車</option>
          <option value="31">自家用軽四輪乗用車</option>
          <option value="33">自家用軽四輪貨物車</option>
        </Select>
      </FormControl>
      <ErrorMessage isUnselected={showErrors && isEmpty(vehicleType)} />
      <h3 ref={carModelRef}>型式</h3>
      <TextField
        id="outlined-basic"
        variant="outlined"
        placeholder=""
        disabled={disabledCheck("carModel")}
        className={`${disabledCheck("carModel") && styles.disabled} ${styles.input}`}
        value={carModel}
        onChange={(event) => {
          setIsNoSearchResult(false)
          setCarModel(extractNumbersAndAlphabets(event.target.value))
          resetByCarModel()
        }}
        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
          if (event.key === "Enter") {
            event.preventDefault()
            setIsNoSearchResult(false)
            const inputValue = (event.target as HTMLInputElement).value
            setCarModel(inputValue)
            getCarList(inputValue)
          }
        }}
        onBlur={(event) => getCarList(event.target.value)}
        inputProps={{ maxLength: 15 }}
      />
      <p className={styles.abridgementNotes}>DBA-などのハイフン（-）より前の部分は省略してご入力ください。</p>
      <ErrorMessage
        noCarModel={isNoSearchResult}
        isEmpty={showErrors && isEmpty(carModel)}
        beginsWithHyphen={hyphenIsAtBeginning(carModel)}
      />
      <h3 ref={carNameRef}>車名</h3>
      <FormControl variant="outlined" className={styles.formControl}>
        {(() => {
          const items = []
          for (let i = 0; i < carNameList.length; i += 1) {
            items.push(
              <option key={i} value={i}>
                {carNameList[i]["自動車-車名"]}
              </option>
            )
          }
          return (
            <Select
              onChange={(event) => {
                setValueAutomaticBraking("")
                resetByCarName()
                currentValueType = -1
                if (carNameList && !isEmpty(event.target.value as string)) {
                  currentValueType = Number(event.target.value)
                  setCarName(carNameList[Number(event.target.value)]["自動車-車名"])
                  currentAbe = Number(carNameList[Number(event.target.value)]["AEB装置"])
                  operationPeriod = carNameList[Number(event.target.value)]["適用期間"]
                  abeVisibleCheck(carNameList[Number(event.target.value)]["適用期間"])
                } else {
                  setCarName("")
                  setValueAbe(0)
                  currentAbe = 0
                }
              }}
              value={currentValueType}
              native
              disabled={disabledCheck("carName")}
              label="carName"
              className={`${disabledCheck("carName") && styles.disabled} ${styles.input}`}
            >
              {carNameList.length === 0 && (
                <option key={-1} value={-1}>
                  {" "}
                </option>
              )}
              {carNameList.length > 1 && (
                <option key={-1} value={-1}>
                  選択してください
                </option>
              )}
              {items}
            </Select>
          )
        })()}
      </FormControl>
      <ErrorMessage isUnselected={showErrors && isEmpty(carName)} />
      {renderFirstInspection()}
      {abeVisible && (
        <>
          <h3 ref={automaticBrakingRef}>自動ブレーキ装置</h3>
          <ButtonGroup
            className={`${disabledCheck("automaticBraking") && styles.disabled} ${styles.buttonGroup}`}
            size="large"
            aria-label="自動ブレーキ装置"
          >
            <Button
              disabled={disabledCheck("automaticBraking")}
              onClick={() => {
                setValueAutomaticBraking("yes")
                setValueAbe(1)
              }}
              className={valueAutomaticBraking === "yes" ? styles.selected : ""}
            >
              あり
            </Button>
            <Button
              disabled={disabledCheck("automaticBraking")}
              onClick={() => {
                setValueAutomaticBraking("no")
                setValueAbe(0)
              }}
              className={valueAutomaticBraking === "no" ? styles.selected : ""}
            >
              なし
            </Button>
            <Button
              disabled={disabledCheck("automaticBraking")}
              onClick={() => {
                setValueAutomaticBraking("unKnown")
                setValueAbe(0)
              }}
              className={valueAutomaticBraking === "unKnown" ? styles.selected : ""}
            >
              わからない
            </Button>
          </ButtonGroup>
          <ErrorMessage isUnselected={showErrors && isEmpty(valueAutomaticBraking)} />
        </>
      )}
      <Grid item xs={12} className={styles.nextButtonWrap}>
        <Box className={styles.commonButtonWrap}>
          <RightArrowButton
            color={Colors.Orange}
            arrowColor={Colors.White}
            text="次　へ"
            onClick={() => {
              goToConditions()
            }}
            isSquareOnPC={false}
          />
        </Box>
      </Grid>
    </>
  )
}
export default QRScanResult
